import "@babel/polyfill";
import "mutationobserver-shim";
import Vue from "vue";
import {BootstrapVue} from 'bootstrap-vue'
import "./plugins/multiselect";
import "./sass/main.scss";
import App from "./App.vue";
import router from "./router";
import {formatDate, formatTime} from "@/formatting/dateFormatter";
import guid from "@/guid";
import {isNullOrWhiteSpace, stringOrEmptyPlaceholder} from "@/extensions/string.helpers";
import "./extensions/array.extensions";
import "./extensions/vue.extensions";
import "./directives/async-src";
import "./directives/validation";

import Component from 'vue-class-component'
import {AuthService} from "@/AuthService";
import {WebStorageStateStore} from "oidc-client";
import {CultureOptions, CultureService, ls} from "@/CultureService";
import {tryHandleError} from "./tryHandleError";
import globalLoader from "@/globalLoader";
import toasts from "@/components/Toasts";
import VueGtm from '@gtm-support/vue2-gtm';

interface Settings extends CultureOptions {
    misApiBaseUrl: string
    jwtBearerAuthority: string,
    termsAndConditionsUrl: string,
    captchaSiteKey: string,
    googleTagId:string
}

export let settings: Settings = {
    misApiBaseUrl: null!,
    jwtBearerAuthority: null!,
    termsAndConditionsUrl: null!,
    changeCultureToEnglishUrl: null!,
    changeCultureToEstonianUrl: null!,
    currentCulture: null!,
    localizedStrings: null!,
    captchaSiteKey: null!,
    googleTagId: null!,
}

const appElementSelector = "#app";

const isOidcFlow =
    window.location.href.toLocaleLowerCase()
        .includes("/oidc-");
const tryToAuthSilently = !isOidcFlow;

fetch("/app-settings")
    .then(responses => responses.json(), error => console.error("Could not retrieve settings. Error: " + error))
    .then((json) => {
        settings = {...settings, ...json} as Settings;
        for (const [key, value] of Object.entries(settings)) {
            if (!value)
                throw new Error("Could not retrieve settings for " + key);
        }
    })
    .then(() => CultureService.initialize(settings))
    .then(async () => await AuthService.initialize({
            /* eslint-disable @typescript-eslint/camelcase */
            authority: settings.jwtBearerAuthority,
            client_id: "VTA.MIS.ClientApp",
            redirect_uri: `https://${window.location.host}/oidc-callback`,
            post_logout_redirect_uri: `https://${window.location.host}`, // Must match exactly with Identity Server configuration or there will be no warning or redirect.
            response_type: "code",
            scope: "openid profile VTA.MIS.API",
            response_mode: "query",
            userStore: new WebStorageStateStore({store: window.sessionStorage}),
            automaticSilentRenew: true,
            accessTokenExpiringNotificationTime: 125, // "2 minutes" is referenced in AuthService warning toast when silent renewal fails.
            silent_redirect_uri: `https://${window.location.host}/oidc-silent-refresh-callback`,
            revokeAccessTokenOnSignout: true,
            monitorSession: true,
            checkSessionInterval: 3000
            /* eslint-enable */
        }, tryToAuthSilently)
    )
    .then(() => {
        // Global methods so we don't need to import them to use them in component's template
        Vue.mixin({
            methods: {
                guid,
                formatDate,
                formatTime,
                isNullOrWhiteSpace,
                stringOrEmptyPlaceholder
            }
        });

        Vue.use(BootstrapVue)
        Component.registerHooks(['beforeRouteEnter']);
        Vue.component("datepicker", () => import("@/components/DatePicker.vue"));
        Vue.component("timepicker", () => import("@/components/TimePicker.vue"));
        Vue.component("dropdown-search", () => import("@/components/DropdownSearch.vue"));
        Vue.prototype.$ls = settings.localizedStrings;
        try {
            Vue.use(VueGtm, {
              id: settings.googleTagId,
              vueRouter: router
            })
          } catch (error) {
            console.warn(error)
          }

        Vue.config.productionTip = false;
        Vue.config.errorHandler = (err : Error, vm : Vue) => {
            if (!tryHandleError(err, vm.$router)) {
                toasts.failure(ls.Global_Error_ClientSideErrorOnPage, 0);
                console.log(err);
            }
        }
    })
    .then(() => {
        globalLoader.startLoading(); // Start the application as loading.
        return new Vue({
            router,
            render: h => h(App)
        }).$mount(appElementSelector);
    })
    .then(vue => {
        window.onerror = function(message, source, lineno, colno, error) {
            if (error && !tryHandleError(error, vue.$router)) {
                toasts.failure(ls.Global_Error_ClientSideErrorOnPage, 0);
                console.log(error);
                console.log(message);
            }
        };
    })
    .catch(error => {
        const appElement = window.document.querySelector(appElementSelector);
        if (appElement) {
            // Get rid of previous HTML nodes (i.e. loader).
            while (appElement.firstChild) {
                appElement.removeChild(appElement.firstChild);
            }

            const errorTextNode = document.createTextNode(ls.Global_Error_ServerUnavailable);
            appElement.appendChild(errorTextNode);

            throw error;
        }
    });